import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type {
  MatchPublic,
  MatchLineupEntryPublic,
  MatchLineupResponse,
  TeamPlayerPublic,
  SportPositions,
  PositionDef,
  LineupAction,
  SelectionPoolPlayer,
  SelectionAvailability,
} from "@dugads/shared";
import { getPositionsForSport, getPositionByNumber } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronRight, Save, Search, RotateCcw, User, Download, Check, X, HelpCircle, Clock, AlertTriangle, FileText, Plus, Trash2 } from "lucide-react";
import type { MatchOfficialRole } from "@dugads/shared";
import { SendMessageButton } from "@/components/send-message-button";

// Local state for a lineup entry being built client-side
interface LineupSlot {
  jersey_number: number;
  player_id: string | null;
  player_name: string | null;
  is_captain: boolean;
  is_vice_captain: boolean;
  covers_positions: number[];
  // 153: BokSmart front-row backup designation (reserves only)
  backup_hooker: boolean;
  backup_prop: boolean;
}

export default function MatchLineupPage() {
  const { matchId, teamId } = useParams<{
    matchId: string;
    teamId: string;
  }>();

  const [match, setMatch] = useState<MatchPublic | null>(null);
  const [teamName, setTeamName] = useState("");
  const [positions, setPositions] = useState<SportPositions | null>(null);
  const [roster, setRoster] = useState<TeamPlayerPublic[]>([]);
  const [slots, setSlots] = useState<LineupSlot[]>([]);
  const [actions, setActions] = useState<LineupAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [startersOpen, setStartersOpen] = useState(true);
  const [reservesOpen, setReservesOpen] = useState(true);
  const [playerSearch, setPlayerSearch] = useState("");
  const [templateAvailable, setTemplateAvailable] = useState(false);
  // 139 -- availability + eligibility, keyed by player_id (empty when the
  // selection pool is unavailable, e.g. caller lacks manage access).
  const [pool, setPool] = useState<Map<string, SelectionPoolPlayer>>(new Map());

  const canEdit = actions.includes("edit");
  const canSendMessage = match?._actions.includes("send_message") ?? false;

  // Build initial empty slots from position definitions
  function buildEmptySlots(sport: SportPositions): LineupSlot[] {
    const starters: LineupSlot[] = sport.positions.map((p) => ({
      jersey_number: p.number,
      player_id: null,
      player_name: null,
      is_captain: false,
      is_vice_captain: false,
      covers_positions: [],
      backup_hooker: false,
      backup_prop: false,
    }));
    const reserves: LineupSlot[] = [];
    for (let i = sport.starters + 1; i <= sport.starters + sport.reserves; i++) {
      reserves.push({
        jersey_number: i,
        player_id: null,
        player_name: null,
        is_captain: false,
        is_vice_captain: false,
        covers_positions: [],
        backup_hooker: false,
        backup_prop: false,
      });
    }
    return [...starters, ...reserves];
  }

  // Merge server lineup data into slots
  function mergeLineup(
    emptySlots: LineupSlot[],
    lineup: MatchLineupEntryPublic[],
  ): LineupSlot[] {
    const byJersey = new Map(lineup.map((e) => [e.jersey_number, e]));
    return emptySlots.map((slot) => {
      const entry = byJersey.get(slot.jersey_number);
      if (entry) {
        return {
          jersey_number: entry.jersey_number,
          player_id: entry.player_id,
          player_name: entry.player_name,
          is_captain: entry.is_captain,
          is_vice_captain: entry.is_vice_captain,
          covers_positions: entry.covers_positions,
          backup_hooker: entry.backup_hooker,
          backup_prop: entry.backup_prop,
        };
      }
      return slot;
    });
  }

  const load = useCallback(async () => {
    if (!matchId || !teamId) return;
    setLoading(true);
    setError(null);

    try {
      // Load match, lineup, and roster in parallel. The selection pool (139) is
      // manage-gated, so a non-manager view degrades gracefully to no badges.
      const [matchRes, lineupRes, rosterRes, poolRes] = await Promise.all([
        api.getMatch(matchId),
        api.getMatchLineup(matchId, teamId),
        api.getTeamPlayers(teamId),
        api.getSelectionPool(matchId, teamId).catch(() => null),
      ]);

      setMatch(matchRes.match);
      setTeamName(lineupRes.team_name);
      setRoster(rosterRes.players);
      setPool(poolRes ? new Map(poolRes.players.map((p) => [p.player_id, p])) : new Map());
      setActions(lineupRes._actions);
      setTemplateAvailable(lineupRes.team_template_available);

      // Get positions from team's sport
      const sportDef = lineupRes.sport_name
        ? getPositionsForSport(lineupRes.sport_name)
        : undefined;

      if (sportDef) {
        setPositions(sportDef);
        const empty = buildEmptySlots(sportDef);
        setSlots(mergeLineup(empty, lineupRes.lineup));
      } else {
        // Fallback: no position layout defined for this sport
        setPositions(null);
        setError(
          lineupRes.sport_name
            ? `No position layout defined for sport "${lineupRes.sport_name}"`
            : "Team has no sport set. Edit the team to assign a sport first.",
        );
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load lineup");
    } finally {
      setLoading(false);
    }
  }, [matchId, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  // Get players not yet assigned to any slot
  const assignedPlayerIds = new Set(
    slots.filter((s) => s.player_id).map((s) => s.player_id!),
  );
  // 139 -- order the available list availability-first when the pool is loaded.
  const AVAILABILITY_RANK: Record<SelectionAvailability, number> = {
    available: 0, maybe: 1, no_reply: 2, not_requested: 2, out: 3,
  };
  const availablePlayers = roster
    .filter((p) => !assignedPlayerIds.has(p.player_id))
    .sort((a, b) => {
      const ra = AVAILABILITY_RANK[pool.get(a.player_id)?.availability ?? "not_requested"];
      const rb = AVAILABILITY_RANK[pool.get(b.player_id)?.availability ?? "not_requested"];
      return ra - rb || a.player_name.localeCompare(b.player_name);
    });

  function assignPlayer(jerseyNumber: number, playerId: string) {
    const player = roster.find((p) => p.player_id === playerId);
    if (!player) return;
    setSlots((prev) =>
      prev.map((s) =>
        s.jersey_number === jerseyNumber
          ? { ...s, player_id: playerId, player_name: player.player_name }
          : s,
      ),
    );
    setSelectedPlayerId(null);
    setDirty(true);
  }

  function removePlayer(jerseyNumber: number) {
    setSlots((prev) =>
      prev.map((s) =>
        s.jersey_number === jerseyNumber
          ? {
              ...s,
              player_id: null,
              player_name: null,
              is_captain: false,
              is_vice_captain: false,
              covers_positions: [],
              backup_hooker: false,
              backup_prop: false,
            }
          : s,
      ),
    );
    setDirty(true);
  }

  function toggleCaptain(jerseyNumber: number) {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.jersey_number === jerseyNumber) {
          if (s.is_captain) return { ...s, is_captain: false };
          // Clear VC if becoming captain
          return { ...s, is_captain: true, is_vice_captain: false };
        }
        // Clear captain from others
        if (s.is_captain) return { ...s, is_captain: false };
        return s;
      }),
    );
    setDirty(true);
  }

  function toggleViceCaptain(jerseyNumber: number) {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.jersey_number === jerseyNumber) {
          if (s.is_vice_captain) return { ...s, is_vice_captain: false };
          // Clear captain if becoming VC
          return { ...s, is_vice_captain: true, is_captain: false };
        }
        // Clear VC from others
        if (s.is_vice_captain) return { ...s, is_vice_captain: false };
        return s;
      }),
    );
    setDirty(true);
  }

  function toggleCoverPosition(jerseyNumber: number, position: number) {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.jersey_number !== jerseyNumber) return s;
        const has = s.covers_positions.includes(position);
        return {
          ...s,
          covers_positions: has
            ? s.covers_positions.filter((p) => p !== position)
            : [...s.covers_positions, position].sort((a, b) => a - b),
        };
      }),
    );
    setDirty(true);
  }

  // 153: toggle a reserve as backup hooker / backup prop (front-row BokSmart)
  function toggleBackupRole(jerseyNumber: number, role: "hooker" | "prop") {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.jersey_number !== jerseyNumber) return s;
        return role === "hooker"
          ? { ...s, backup_hooker: !s.backup_hooker }
          : { ...s, backup_prop: !s.backup_prop };
      }),
    );
    setDirty(true);
  }

  async function handleLoadTemplate() {
    if (!teamId || !positions) return;
    setError(null);
    try {
      const posRes = await api.getTeamPositions(teamId);
      if (posRes.positions.length === 0) {
        setError("Team has no default positions set");
        return;
      }
      const empty = buildEmptySlots(positions);
      const merged = mergeLineup(
        empty,
        posRes.positions.map((p) => ({
          ...p,
          match_id: "",
          // Strip secondary positions from starters (match lineup only allows covers on reserves)
          covers_positions: p.jersey_number > positions.starters ? p.covers_positions : [],
          // Team-position templates do not carry backup designation
          backup_hooker: false,
          backup_prop: false,
        })),
      );
      setSlots(merged);
      setDirty(true);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load template");
    }
  }

  function handleReset() {
    if (positions) {
      setSlots(buildEmptySlots(positions));
      setSelectedPlayerId(null);
      setDirty(true);
    }
  }

  async function handleSave() {
    if (!matchId || !teamId) return;
    const filledSlots = slots.filter((s) => s.player_id);
    if (filledSlots.length === 0) {
      setError("Select at least one player");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.updateMatchLineup(matchId, teamId, {
        lineup: filledSlots.map((s) => ({
          player_id: s.player_id!,
          jersey_number: s.jersey_number,
          is_captain: s.is_captain,
          is_vice_captain: s.is_vice_captain,
          covers_positions: s.covers_positions,
          backup_hooker: s.backup_hooker,
          backup_prop: s.backup_prop,
        })),
      });
      setDirty(false);
      // Reload to get server state
      await load();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save lineup");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="match-lineup" data-state="loading">Loading...</p>;
  }

  if (!match || !positions) {
    return (
      <div className="flex flex-col gap-4" data-page="match-lineup" data-state="error" data-error>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Link
          to={`/matches/${matchId}`}
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Match
        </Link>
      </div>
    );
  }

  const starterSlots = slots.filter(
    (s) => s.jersey_number <= positions.starters,
  );
  const reserveSlots = slots.filter(
    (s) => s.jersey_number > positions.starters,
  );

  // Group starters by row for formation layout
  const rows = new Map<number, { slot: LineupSlot; pos: PositionDef }[]>();
  for (const slot of starterSlots) {
    const pos = getPositionByNumber(positions, slot.jersey_number);
    if (!pos) continue;
    const arr = rows.get(pos.row) ?? [];
    arr.push({ slot, pos });
    rows.set(pos.row, arr);
  }
  // Sort each row by col
  for (const arr of rows.values()) {
    arr.sort((a, b) => a.pos.col - b.pos.col);
  }
  const sortedRows = [...rows.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <div className="flex flex-col gap-6" data-page="match-lineup" data-state="ready">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={`/matches/${matchId}`}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">Team Sheet</h1>
            <p className="text-muted-foreground">
              {match.name} &middot; {teamName}
            </p>
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {templateAvailable && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadTemplate}
                disabled={saving}
              >
                <Download className="mr-2 h-4 w-4" />
                Load Template
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !dirty}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>

      {/* 154 -- match officials editor + BokSmart form generation */}
      {matchId && <MatchOfficialsEditor matchId={matchId} canEdit={canEdit} />}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Desktop: players left, field right. Mobile: stacked */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Available Players -- left column on desktop, below on mobile */}
        {canEdit && (
          <div className="order-2 flex flex-col rounded-lg border border-border p-4 lg:order-1 lg:w-64 lg:shrink-0 lg:self-start lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)]" data-region="sidebar">
            <h2 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Available ({availablePlayers.length})
            </h2>
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search players..."
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            {availablePlayers.length === 0 && (
              <p className="text-sm text-muted-foreground">
                All players assigned
              </p>
            )}
            <div className="flex flex-col gap-2 overflow-y-auto">
              {availablePlayers.filter((p) => p.player_name.toLowerCase().includes(playerSearch.toLowerCase())).map((p) => {
                const info = pool.get(p.player_id);
                return (
                  <button
                    key={p.player_id}
                    type="button"
                    onClick={() =>
                      setSelectedPlayerId(
                        selectedPlayerId === p.player_id ? null : p.player_id,
                      )
                    }
                    className={`flex flex-col gap-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      selectedPlayerId === p.player_id
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50 hover:bg-muted"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{p.player_name}</span>
                      {info && <AvailabilityBadge status={info.availability} />}
                      {/* 146/147/155/158 -- hard block (concussion not cleared, A/B/C cross-team lock, incomplete indemnity, or play-down A/B-squad lock) */}
                      {info && (info.injury_blocking || info.cross_team_block || info.indemnity_blocking || info.override_block) && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                          {info.injury_blocking ? "Blocked" : info.cross_team_block ? "In higher team" : info.indemnity_blocking ? "Indemnity" : "Play-down"}
                        </span>
                      )}
                    </span>
                    {info && !info.eligible && info.eligibility_reason && (
                      <span
                        className={`flex items-center gap-1 text-[11px] font-normal ${
                          info.injury_blocking || info.cross_team_block || info.indemnity_blocking || info.override_block ? "text-red-600" : "text-amber-600"
                        }`}
                      >
                        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                        {info.eligibility_reason}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedPlayerId && (
              <p className="mt-2 shrink-0 text-xs text-muted-foreground">
                Click an empty position to place the selected player
              </p>
            )}
          </div>
        )}

        {/* Formation + Reserves -- right column on desktop */}
        <div className="order-1 flex min-w-0 flex-1 flex-col gap-6 lg:order-2">
          {/* Formation Field */}
          <div className="rounded-lg border border-border bg-green-900/10 dark:bg-green-900/20" data-region="formation">
            <button
              type="button"
              onClick={() => setStartersOpen((v) => !v)}
              className="flex w-full items-center gap-2 p-4 text-left"
            >
              {startersOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Starting XV ({starterSlots.filter((s) => s.player_id).length}/{positions.starters})
              </h2>
            </button>
            {startersOpen && (
              <div className="flex flex-col items-center gap-3 px-4 pb-4">
                {sortedRows.map(([rowIdx, rowItems]) => (
                  <div
                    key={rowIdx}
                    className="flex flex-wrap justify-center gap-2"
                  >
                    {rowItems.map(({ slot, pos }) => (
                      <PlayerCard
                        key={slot.jersey_number}
                        slot={slot}
                        position={pos}
                        isSelected={selectedPlayerId !== null && !slot.player_id}
                        canEdit={canEdit}
                        canSendMessage={canSendMessage}
                        onAssign={() => {
                          if (selectedPlayerId && !slot.player_id) {
                            assignPlayer(slot.jersey_number, selectedPlayerId);
                          }
                        }}
                        onRemove={() => removePlayer(slot.jersey_number)}
                        onToggleCaptain={() => toggleCaptain(slot.jersey_number)}
                        onToggleVC={() => toggleViceCaptain(slot.jersey_number)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reserves */}
          <div className="rounded-lg border border-border" data-region="roster">
            <button
              type="button"
              onClick={() => setReservesOpen((v) => !v)}
              className="flex w-full items-center gap-2 p-4 text-left"
            >
              {reservesOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Replacements ({reserveSlots.filter((s) => s.player_id).length}/{positions.reserves})
              </h2>
            </button>
            {reservesOpen && (
              <div className="grid grid-cols-1 gap-2 px-4 pb-4 sm:grid-cols-2 lg:grid-cols-3">
                {reserveSlots.map((slot) => (
                  <ReserveCard
                    key={slot.jersey_number}
                    slot={slot}
                    positions={positions}
                    isSelected={selectedPlayerId !== null && !slot.player_id}
                    canEdit={canEdit}
                    canSendMessage={canSendMessage}
                    onAssign={() => {
                      if (selectedPlayerId && !slot.player_id) {
                        assignPlayer(slot.jersey_number, selectedPlayerId);
                      }
                    }}
                    onRemove={() => removePlayer(slot.jersey_number)}
                    onToggleCaptain={() => toggleCaptain(slot.jersey_number)}
                    onToggleVC={() => toggleViceCaptain(slot.jersey_number)}
                    onToggleCover={(pos) =>
                      toggleCoverPosition(slot.jersey_number, pos)
                    }
                    onToggleBackup={(role) =>
                      toggleBackupRole(slot.jersey_number, role)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Availability badge (139) ---

function AvailabilityBadge({ status }: { status: SelectionAvailability }) {
  const meta: Record<SelectionAvailability, { label: string; cls: string; Icon: typeof Check } | null> = {
    available: { label: "In", cls: "bg-[#E1F5EE] text-[#085041]", Icon: Check },
    maybe: { label: "Maybe", cls: "bg-[#FAEEDA] text-[#633806]", Icon: HelpCircle },
    out: { label: "Out", cls: "bg-[#FCEBEB] text-[#791F1F]", Icon: X },
    no_reply: { label: "No reply", cls: "bg-[#F1EFE8] text-[#444441]", Icon: Clock },
    not_requested: null, // nothing to show until availability is requested
  };
  const m = meta[status];
  if (!m) return null;
  return (
    <span className={`ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${m.cls}`}>
      <m.Icon className="h-2.5 w-2.5" aria-hidden /> {m.label}
    </span>
  );
}

// --- Player Card (Stormers broadcast style) ---

function PlayerCard({
  slot,
  position,
  isSelected,
  canEdit,
  canSendMessage,
  onAssign,
  onRemove,
  onToggleCaptain,
  onToggleVC,
}: {
  slot: LineupSlot;
  position: PositionDef;
  isSelected: boolean;
  canEdit: boolean;
  canSendMessage: boolean;
  onAssign: () => void;
  onRemove: () => void;
  onToggleCaptain: () => void;
  onToggleVC: () => void;
}) {
  const isEmpty = !slot.player_id;
  const surname = slot.player_name
    ? slot.player_name.split(" ").pop()?.toUpperCase() ?? ""
    : "";

  if (isEmpty) {
    return (
      <button
        type="button"
        onClick={canEdit ? onAssign : undefined}
        disabled={!canEdit || !isSelected}
        className={`flex w-40 flex-col items-center gap-0.5 rounded-lg border-2 border-dashed px-3 py-2 text-center transition-colors ${
          isSelected && canEdit
            ? "border-primary bg-primary/5 cursor-pointer"
            : "border-border/50 opacity-60"
        }`}
      >
        <span className="text-lg font-bold text-muted-foreground">
          {slot.jersey_number}
        </span>
        <span className="text-xs text-muted-foreground">
          {position.abbreviation}
        </span>
      </button>
    );
  }

  return (
    <div className="group relative flex w-40 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 shadow-sm" data-item={`player-${slot.jersey_number}`}>
      {/* Avatar placeholder */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <User className="h-4 w-4" />
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1">
          <span className="truncate text-sm font-bold">
            {surname}
          </span>
          {slot.is_captain && (
            <span className="text-xs font-medium text-primary">(c)</span>
          )}
          {slot.is_vice_captain && (
            <span className="text-xs font-medium text-primary">(vc)</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {position.name}
        </p>
      </div>
      {/* Jersey number */}
      <span className="text-lg font-bold text-muted-foreground">
        {slot.jersey_number}
      </span>
      {/* Hover actions */}
      {(canEdit || canSendMessage) && (
        <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-background/90 opacity-0 transition-opacity group-hover:opacity-100">
          {canSendMessage && slot.player_id && slot.player_name && (
            <SendMessageButton
              contextType="player"
              contextId={slot.player_id}
              contextLabel={slot.player_name}
            />
          )}
          {canEdit && (
            <>
              <button
                type="button"
                onClick={onToggleCaptain}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                  slot.is_captain
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-primary/10"
                }`}
              >
                C
              </button>
              <button
                type="button"
                onClick={onToggleVC}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                  slot.is_vice_captain
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-primary/10"
                }`}
              >
                VC
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="rounded bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
              >
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// --- Reserve Card ---

function ReserveCard({
  slot,
  positions: sport,
  isSelected,
  canEdit,
  canSendMessage,
  onAssign,
  onRemove,
  onToggleCaptain,
  onToggleVC,
  onToggleCover,
  onToggleBackup,
}: {
  slot: LineupSlot;
  positions: SportPositions;
  isSelected: boolean;
  canEdit: boolean;
  canSendMessage: boolean;
  onAssign: () => void;
  onRemove: () => void;
  onToggleCaptain: () => void;
  onToggleVC: () => void;
  onToggleCover: (pos: number) => void;
  onToggleBackup: (role: "hooker" | "prop") => void;
}) {
  const isEmpty = !slot.player_id;
  const surname = slot.player_name
    ? slot.player_name.split(" ").pop()?.toUpperCase() ?? ""
    : "";

  if (isEmpty) {
    return (
      <button
        type="button"
        onClick={canEdit ? onAssign : undefined}
        disabled={!canEdit || !isSelected}
        className={`flex items-center gap-3 rounded-lg border-2 border-dashed px-3 py-2 transition-colors ${
          isSelected && canEdit
            ? "border-primary bg-primary/5 cursor-pointer"
            : "border-border/50 opacity-60"
        }`}
      >
        <span className="text-lg font-bold text-muted-foreground">
          #{slot.jersey_number}
        </span>
        <span className="text-sm text-muted-foreground">Reserve</span>
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3 shadow-sm" data-item={`reserve-${slot.jersey_number}`}>
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <User className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1">
            <span className="truncate text-sm font-bold">{surname}</span>
            {slot.is_captain && (
              <span className="text-xs font-medium text-primary">(c)</span>
            )}
            {slot.is_vice_captain && (
              <span className="text-xs font-medium text-primary">(vc)</span>
            )}
          </div>
        </div>
        <span className="text-lg font-bold text-muted-foreground">
          #{slot.jersey_number}
        </span>
      </div>
      {/* Covers positions */}
      {canEdit && (
        <div className="flex flex-wrap gap-1">
          {sport.positions.map((p) => (
            <button
              key={p.number}
              type="button"
              onClick={() => onToggleCover(p.number)}
              className={`rounded px-1.5 py-0.5 text-xs transition-colors ${
                slot.covers_positions.includes(p.number)
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-primary/10"
              }`}
              title={`Covers #${p.number} ${p.name}`}
            >
              {p.number}
            </button>
          ))}
        </div>
      )}
      {!canEdit && slot.covers_positions.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Covers: {slot.covers_positions.map((n) => `#${n}`).join(", ")}
        </p>
      )}
      {/* 153: front-row backup designation (BokSmart) */}
      {canEdit && (
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => onToggleBackup("hooker")}
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
              slot.backup_hooker
                ? "bg-[#0C447C] text-white"
                : "bg-muted text-muted-foreground hover:bg-primary/10"
            }`}
            title="Designate as backup hooker for the BokSmart form"
          >
            Bk Hooker
          </button>
          <button
            type="button"
            onClick={() => onToggleBackup("prop")}
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
              slot.backup_prop
                ? "bg-[#0C447C] text-white"
                : "bg-muted text-muted-foreground hover:bg-primary/10"
            }`}
            title="Designate as backup prop for the BokSmart form"
          >
            Bk Prop
          </button>
        </div>
      )}
      {!canEdit && (slot.backup_hooker || slot.backup_prop) && (
        <p className="text-xs text-muted-foreground">
          Backup: {[slot.backup_hooker && "hooker", slot.backup_prop && "prop"].filter(Boolean).join(", ")}
        </p>
      )}
      {/* Actions */}
      {(canEdit || canSendMessage) && (
        <div className="flex gap-1">
          {canSendMessage && slot.player_id && slot.player_name && (
            <SendMessageButton
              contextType="player"
              contextId={slot.player_id}
              contextLabel={slot.player_name}
            />
          )}
          {canEdit && (
            <>
              <button
                type="button"
                onClick={onToggleCaptain}
                className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                  slot.is_captain
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-primary/10"
                }`}
              >
                C
              </button>
              <button
                type="button"
                onClick={onToggleVC}
                className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                  slot.is_vice_captain
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-primary/10"
                }`}
              >
                VC
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="rounded bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive hover:bg-destructive/20"
              >
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// --- 154: Match officials editor + BokSmart form button ---

interface OfficialRow {
  role: MatchOfficialRole;
  name: string;
  boksmart_number: string;
}

function MatchOfficialsEditor({ matchId, canEdit }: { matchId: string; canEdit: boolean }) {
  const [rows, setRows] = useState<OfficialRow[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getMatchOfficials(matchId);
      setRows(
        res.officials.map((o) => ({
          role: o.role,
          name: o.name,
          boksmart_number: o.boksmart_number ?? "",
        })),
      );
    } catch {
      // officials are optional; ignore load errors (e.g. no access)
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load]);

  function addRow(role: MatchOfficialRole) {
    setRows((r) => [...r, { role, name: "", boksmart_number: "" }]);
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api.updateMatchOfficials(matchId, {
        officials: rows
          .filter((r) => r.name.trim())
          .map((r) => ({
            role: r.role,
            name: r.name.trim(),
            boksmart_number: r.boksmart_number.trim() || null,
          })),
      });
      await load();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to save officials");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-border" data-region="match-officials">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 p-4 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Match officials &amp; BokSmart ({rows.length})
        </h2>
        <Link
          to={`/matches/${matchId}/boksmart`}
          onClick={(e) => e.stopPropagation()}
          className={buttonVariants({ variant: "outline", size: "sm" }) + " ml-auto"}
          data-action="generate-boksmart"
        >
          <FileText className="mr-2 h-4 w-4" />
          Generate BokSmart form
        </Link>
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">No officials captured yet.</p>
          )}
          {rows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="w-32 shrink-0 text-xs text-muted-foreground">
                {r.role === "referee" ? "Referee" : "Assistant ref"}
              </span>
              <input
                type="text"
                placeholder="Name"
                value={r.name}
                disabled={!canEdit}
                onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-primary focus:outline-none"
              />
              <input
                type="text"
                placeholder="BokSmart no. (optional)"
                value={r.boksmart_number}
                disabled={!canEdit}
                onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, boksmart_number: e.target.value } : x)))}
                className="w-44 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-primary focus:outline-none"
              />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  aria-label="Remove official"
                  className="rounded bg-destructive/10 p-1 text-destructive hover:bg-destructive/20"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => addRow("referee")} disabled={rows.some((r) => r.role === "referee")}>
                <Plus className="mr-2 h-4 w-4" /> Referee
              </Button>
              <Button variant="outline" size="sm" onClick={() => addRow("assistant_referee")}>
                <Plus className="mr-2 h-4 w-4" /> Assistant referee
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Save officials"}
              </Button>
            </div>
          )}
          {err && <p className="text-sm text-destructive">{err}</p>}
        </div>
      )}
    </div>
  );
}
